import { useEffect } from 'react';

/**
 * ehr#231 - freeze the page behind a modal. While any lock is held the page's own
 * scrollbar is hidden (body overflow:hidden), so the wheel or a touchpad no longer
 * scrolls the list behind a pop-up. The scrollbar's width is padded back onto the body
 * so the page doesn't jump sideways when it disappears.
 *
 * Locks are counted: two overlays open at once (the client drawer plus a settings
 * window) keep the page frozen until the last one closes, and the page's original
 * inline styles come back exactly as they were.
 */
type LockDoc = Pick<Document, 'body' | 'documentElement' | 'defaultView'>;

let holders = 0;
let saved: { overflow: string; paddingRight: string } | null = null;

export function lockScroll(doc: LockDoc = document): () => void {
  if (holders === 0) {
    const style = doc.body.style;
    saved = { overflow: style.overflow, paddingRight: style.paddingRight };
    const view = doc.defaultView;
    const bar = view ? view.innerWidth - doc.documentElement.clientWidth : 0;
    style.overflow = 'hidden';
    if (bar > 0) {
      const base = parseFloat(view?.getComputedStyle(doc.body).paddingRight ?? '') || 0;
      style.paddingRight = `${base + bar}px`;
    }
  }
  holders += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders -= 1;
    if (holders === 0 && saved) {
      doc.body.style.overflow = saved.overflow;
      doc.body.style.paddingRight = saved.paddingRight;
      saved = null;
    }
  };
}

/** Hold a scroll lock while `active` is true. */
export function useScrollLock(active: boolean): void {
  useEffect(() => (active ? lockScroll() : undefined), [active]);
}
